import {DumpsterFire} from "../DumpsterFire";
import {DFGameConfig} from "../DFGameConfig";
import {DFAvalancheReelModel} from "./DFAvalancheReelModel";
import {ISymbol} from "../../core/reel/simplereel/IReelData";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {gsap, TimelineLite} from "gsap";
import {SYMBOL_NAME} from "./DumpsterFireSymbol";
import {StageLayer} from "@nolimitcity/slot-keypad/bin/core/app/StageLayer";

export class DFReelAreaView extends PIXI.Container implements StageLayer {

    public readonly DROP_IN_REEL_DELAY: number = 0.04;
    private readonly COLLAPSE_REEL_DELAY: number = 0;
    private readonly FAST_DROP_IN_REEL_DELAY: number = 0;
    private _bitmapWinTextArray: PIXI.Sprite[] = []
    public needFillUpReels: boolean[];

    constructor() {
        super();
        this.init();
    }

    public reInit() {
        for (let i: number = 0; i < this._mainReelModels.length; i++) {
            const reel = this._mainReelModels[i];
            reel.clear();
            reel.init(DumpsterFire.model.data.reels[i]);
        }
    }

    get hasAnticipation(): boolean {
        return !DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
    }

    public get fillUpReels(): boolean[] {
        return this.needFillUpReels;
    }

    public addBonusInGameInitialMulti(): gsap.core.Timeline {
        const tl = gsap.timeline();
        for (let reelId: number = 0; reelId < DumpsterFire.model.data.reels.length; reelId++) {
            for (let rowId: number = 0; rowId < DumpsterFire.model.data.reels[reelId].length; rowId++) {
                let multiValue: number = DumpsterFire.model.data.positionMultipliers[reelId][rowId];
                if (multiValue > 1) {
                    tl.add([DumpsterFire.bgMulti.showMultipliers(reelId, rowId, multiValue),
                        DumpsterFire.bgFinalMulti.showFinalMultipliers(reelId, rowId, multiValue)
                    ], 0);
                }
            }
        }
        return tl;
    }

    public addBBInitialMulti(): gsap.core.Timeline {
        const tl = gsap.timeline();
        let multiValue: number = 0;
        const activeBetFeature = DumpsterFire.apiPlugin.betFeatureController.getActiveBetFeature();
        if (activeBetFeature?.name === "BOOSTED_BET_STATE_2") {
            multiValue = 2;
        } else if (activeBetFeature?.name === "BOOSTED_BET_STATE_64") {
            multiValue = 64;
        } else if (activeBetFeature?.name === "BOOSTED_BET_STATE_1024") {
            multiValue = 1024;
        }
        if (DumpsterFire.model.isBBChanged || DumpsterFire.model.boostedBetName === "" || DumpsterFire.model.isNotNoWinRound || DumpsterFire.model.boostedBetName !== activeBetFeature?.name || DumpsterFire.model.inActionSpin) {
            for (let reelId: number = 0; reelId < DumpsterFire.model.data.reels.length; reelId++) {
                for (let rowId: number = 0; rowId < DumpsterFire.model.data.reels[reelId].length; rowId++) {
                    if (multiValue > 1) {
                        tl.add([
                            DumpsterFire.bgMulti.showMultipliers(reelId, rowId, multiValue),
                            DumpsterFire.bgFinalMulti.showFinalMultipliers(reelId, rowId, multiValue)
                        ], 0);
                    }
                }
            }
        }
        return tl;
    }

    public addAllSymMulti(data: number[][]): gsap.core.Timeline {
        const tl = gsap.timeline();
        for (let reelId: number = 0; reelId < DumpsterFire.model.data.reels.length; reelId++) {
            for (let rowId: number = 0; rowId < DumpsterFire.model.data.reels[reelId].length; rowId++) {
                const multiValue: number = data[reelId][rowId];
                if (multiValue > 1) {
                    tl.add([DumpsterFire.bgMulti.showMultipliers(reelId, rowId, multiValue),
                        DumpsterFire.bgFinalMulti.showFinalMultipliers(reelId, rowId, multiValue)
                    ], 0);
                }
                if (multiValue === 8192) {
                    DumpsterFire.model.hitMaxMulti.push(6 * reelId + rowId);
                }
            }
        }
        return tl;
    }

    public addSymMulti(data: number[][], reelId: number, rowId: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        const multiValue: number = data[reelId][rowId];
        tl.add([DumpsterFire.bgMulti.showMultipliers(reelId, rowId, multiValue),
            DumpsterFire.bgFinalMulti.showFinalMultipliers(reelId, rowId, multiValue)
        ], 0);
        if (multiValue === 8192) {
            DumpsterFire.model.hitMaxMulti.push(6 * reelId + rowId);
        }
        return tl;
    }

    private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371; // Radius of the Earth in kilometers
        const φ1 = this.toRadians(lat1); // Latitude of point 1 in radians
        const φ2 = this.toRadians(lat2); // Latitude of point 2 in radians
        const Δφ = this.toRadians(lat2 - lat1); // Difference in latitudes
        const Δλ = this.toRadians(lon2 - lon1); // Difference in longitudes

        // Haversine formula
        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        // Distance in kilometers
        const distance = R * c;
        return distance;
    }

    private toRadians(degrees: number): number {
        return degrees * Math.PI / 180;
    }

    public infectedSymbol(multiValue: number, prevMultiPosValue: number, symbolNumber: number, posX: number, posY: number, count: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let largestValue: number = 2;
        let delay = 0.8;
        let firstShot: boolean = false;
        tl.add(DumpsterFire.winMultiView.addSpriteToMatrix(posX, posY, multiValue * prevMultiPosValue), 0.1)
        if (symbolNumber > 0) {
            for (let item: number = symbolNumber - 1; item > -1; item--) {
                const prevPos: number = DumpsterFire.model.multiOnXIArray[item][1],
                    prevReelId: number = Math.floor(prevPos / 6),
                    prevRowId: number = Math.floor(prevPos % 6),
                    prevMultiValue: number = DumpsterFire.model.multiOnXIArray[item][0];
                let prevMultiPosValue: number = DumpsterFire.preMultiPosArray[prevReelId][prevRowId];
                multiValue *= prevMultiValue;
                if (multiValue * prevMultiPosValue > largestValue) {
                    largestValue = multiValue * prevMultiPosValue
                }
                const d: number = this.calculateDistance(posX, posY, prevReelId, prevRowId);
                let duration: number = 1.4;
                if (d < 220) {
                    duration = 0.8
                } else if (d > 220 && d < 400) {
                    duration = 1.2
                }
                if (duration > delay) {
                    delay = duration;
                }
                tl.add(DumpsterFire.xInfectiousWaysView.playXIAnimation(posX, posY, prevReelId, prevRowId, duration), 0);
                tl.add([() => {
                    DumpsterFire.allReelsView.removeWinMulti()
                },
                    DumpsterFire.winMultiView.addSpriteToMatrix(prevReelId, prevRowId, multiValue * prevMultiPosValue),
                    DumpsterFire.bgMulti.showMultipliers(prevReelId, prevRowId, multiValue * prevMultiPosValue),
                    DumpsterFire.bgFinalMulti.showFinalMultipliers(prevReelId, prevRowId, multiValue * prevMultiPosValue)
                ], `<${duration - 0.2}`);
                if (count === 0 && !firstShot) {
                    tl.add(() => {
                        DumpsterFire.allReelsView.removeWinMulti()
                    }, `<${duration - 0.2}`);
                    firstShot = true;
                }
            }
        }
        for (let reelId: number = 0; reelId < DumpsterFire.model.data.reels.length; reelId++) {
            for (let rowId: number = 0; rowId < DumpsterFire.model.data.reels[reelId].length; rowId++) {
                let symbolName: string = DumpsterFire.model.reels[reelId][rowId];
                let prevMultiPosValue: number = DumpsterFire.preMultiPosArray[reelId][rowId];
                const d: number = this.calculateDistance(posX, posY, reelId, rowId);
                let duration: number = 1.0;
                if (d < 220) {
                    duration = 0.6
                } else if (d > 220 && d < 350) {
                    duration = 0.8
                }
                if (duration > delay) {
                    delay = duration;
                }
                if (DumpsterFire.model.data.xWaysSymbol === symbolName) {
                    if (multiValue * prevMultiPosValue > largestValue) {
                        largestValue = multiValue * prevMultiPosValue
                    }
                    if (multiValue > 1) {
                        tl.add(DumpsterFire.xInfectiousWaysView.playXIAnimation(posX, posY, reelId, rowId, duration), 0);
                        tl.add([
                            () => {
                                DumpsterFire.allReelsView.removeWinMulti()
                            },
                            DumpsterFire.winMultiView.addSpriteToMatrix(reelId, rowId, multiValue * prevMultiPosValue),
                            DumpsterFire.bgMulti.showMultipliers(reelId, rowId, multiValue * prevMultiPosValue),
                            DumpsterFire.bgFinalMulti.showFinalMultipliers(reelId, rowId, multiValue * prevMultiPosValue),
                        ], `<${duration - 0.2}`);
                        if (count === 0 && !firstShot) {
                            tl.add(() => {
                                DumpsterFire.allReelsView.removeWinMulti()
                            }, `<${duration - 0.2}`);
                            firstShot = true;
                        }
                    }
                }
                if (symbolName === SYMBOL_NAME.X_WAYS) {
                    if (multiValue * prevMultiPosValue > largestValue) {
                        largestValue = multiValue * prevMultiPosValue
                    }
                    if (multiValue > 1) {
                        tl.add(DumpsterFire.xInfectiousWaysView.playXIAnimation(posX, posY, reelId, rowId, duration), 0);
                        tl.add([
                            () => {
                                DumpsterFire.allReelsView.removeWinMulti()
                            },
                            DumpsterFire.winMultiView.addSpriteToMatrix(reelId, rowId, multiValue * prevMultiPosValue * DumpsterFire.model.data.xWaysMultipliers[reelId][rowId]),
                            DumpsterFire.bgMulti.showMultipliers(reelId, rowId, multiValue * prevMultiPosValue * DumpsterFire.model.data.xWaysMultipliers[reelId][rowId]),
                            DumpsterFire.bgFinalMulti.showFinalMultipliers(reelId, rowId, multiValue * prevMultiPosValue * DumpsterFire.model.data.xWaysMultipliers[reelId][rowId]),
                        ], `<${duration - 0.2}`);
                        if (count === 0 && !firstShot) {
                            tl.add(() => {
                                DumpsterFire.allReelsView.removeWinMulti()
                            }, `<${duration - 0.2}`);
                            firstShot = true;
                        }
                    }
                }
            }
        }
        tl.add(() => {
            DumpsterFire.soundPlayer.getXFeatureMultiSound(largestValue);
        }, '>');
        return tl;
    }

    public showWinMultipliers(reelId: number, rowId: number, multiValue: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (multiValue > 1) {
            if (multiValue > 8192) {
                multiValue = 8192
            }
            const multiText = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures(`multi${multiValue}`)[0]);
            tl.add([() => {
                multiText.anchor.set(0.5);
                multiText.visible = true;
                multiText.alpha = 0
                multiText.position.set(DFGameConfig.symbolWidth * reelId, (DFGameConfig.symbolHeight * rowId));
                this.addChild(multiText);
            }]);
            tl.to(multiText, {duration: 0.1, alpha: 1});
            // DumpsterFire.model._matrix[reelId][rowId].push(multiText);
            this._bitmapWinTextArray.push(multiText);
        }
        return tl;
    }

    public removeWinMulti(): void {
        const tl: TimelineLite = new TimelineLite();
        this._bitmapWinTextArray.forEach((item: PIXI.Sprite) => {
            tl.to(item, {duration: 0.1, alpha: 0});
            tl.add(() => {
                this.removeChild(item);
            }, 0.1)
        })
    }

    public removeAllMulti() {
        this.removeWinMulti();
        DumpsterFire.winMultiView.removeAllSprites();
    }

    private _mainReelModels: DFAvalancheReelModel[];
    get mainReelModels(): DFAvalancheReelModel[] {
        return this._mainReelModels;
    }

    public getSymbol(pos: number[]): ISymbol {
        return this.mainReelModels[pos[0]].reelView.getSymbol(pos[1]);
    }

    get isFastSpin(): boolean {
        return DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
    }

    public collapse(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [];
        let delay: number = 0;
        DumpsterFire.allReelsView.mainReelModels.forEach((reel: DFAvalancheReelModel) => {
            if (reel.reelView.needCollapse) {
                animations.push(reel.reelView.collapseReel(delay));
                delay += this.COLLAPSE_REEL_DELAY;
            }
        });
        return animations;
    }

    public fillUp(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [];
        let delay: number = 0;
        this.mainReelModels.forEach((reel: DFAvalancheReelModel, i: number) => {
            const currentReel: number = JSON.parse(JSON.stringify(i));
            const fastSpin: boolean = DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
            let stopDelay: number = DumpsterFire.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? this.FAST_DROP_IN_REEL_DELAY : this.DROP_IN_REEL_DELAY;
            if (reel.reelView.getFillUp(i)) {
                if (currentReel + 1 < DFGameConfig.totalReels && DumpsterFire.model.nearWinReels[currentReel + 1] && DumpsterFire.model.scatterCount <= 4) {
                    stopDelay += fastSpin ? 0 : 0.7;
                }
            }
            animations.push(reel.reelView.getFillUpReelAnimation(delay, i, DumpsterFire.model.nearWinReels[i], fastSpin));
            delay += stopDelay;
        });
        return animations;
    }

    public getSymbolAtPosition(reel: number, position: number): ISymbol {
        return this.mainReelModels[reel].getSymbol(position);
    }

    private init(): void {
        this.name = "ReelAreaView";
        this._mainReelModels = [];
        let tempReel: string[];
        for (let i: number = 0; i < DFGameConfig.defaultGameAreaSize.length; i++) {
            tempReel = [];
            const reelModel: DFAvalancheReelModel = new DFAvalancheReelModel(i);
            this._mainReelModels.push(reelModel);
            this.addChild(reelModel.reelView);
            for (let j: number = 0; j < DumpsterFire.model.data.reels[i].length; j++) {
                tempReel.push(DumpsterFire.model.data.reels[i][j]);
            }
            reelModel.init(tempReel);
        }
        const mainReelsMask: PIXI.Graphics = new PIXI.Graphics();
        mainReelsMask.name = "reelAreaMask";
        mainReelsMask.beginFill(0xFFFF0B, 1);
        mainReelsMask.drawRect(-85, -57, this.getReelAreaWidth() + 65, this.getReelAreaHeight() + 5);
        mainReelsMask.position = DFGameConfig.MAIN_REELS_VIEW_MASK_POSITION;
        // todo enable reel area masking
        this.addChild(mainReelsMask);
        this.mask = mainReelsMask;
        this.position.x = 60;
        this.position.y = 130;
    }

    public getShakeAnimation(x: number, y: number, hertz: number, intensity: number, duration: number): gsap.core.Timeline {
        let startPosY: number,
            startPosX: number,
            t: boolean = true;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                startPosY = this.y;
                startPosX = this.x;
            },
            onComplete: () => {
                this.x = startPosX;
                this.y = startPosY;
            },
            onUpdate: () => {
                const lt: number = Math.random() * 2,
                    et: number = Math.random();
                if (t) {
                    t = false;
                    return;
                }
                this.y = startPosY + Math.cos(lt * hertz) * (intensity * et) * y;
                this.x = startPosX + (Math.sin(lt * hertz) * (intensity * et)) * x;
            }
        });
        tl.to(this, {duration: duration});
        return tl;
    }

    private getReelAreaWidth(): number {
        return (DFGameConfig.symbolWidth * DFGameConfig.totalReels);
    }

    private getReelAreaHeight(): number {
        return DFGameConfig.symbolHeight * DFGameConfig.totalSymsPerReel;
    }
}